import React, { useState } from 'react';
import { X, Upload, FileSpreadsheet, Download, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        toast.error('Please upload an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setUploadResult(null);
    }
  };

  const downloadTemplate = () => {
    const template = `firstName,lastName,mobile,email,companyName,gstin,billingAddress,shippingAddress,outstandingAmount
John,Doe,9876543210,john@example.com,ABC Pvt Ltd,22AAAAA0000A1Z5,"123 Main St, Delhi, Delhi, 110001","123 Main St, Delhi, Delhi, 110001",1000
Jane,Smith,9876543211,jane@example.com,XYZ Corp,27BBBBB1111B2Z6,"456 Park Ave, Mumbai, Maharashtra, 400001","456 Park Ave, Mumbai, Maharashtra, 400001",0`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customer_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('customerAdminToken');

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${backendUrl}/api/customer-admin/customers/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `Server error: ${response.status}` }));
        toast.error(errorData.detail || 'Failed to upload file');
        return;
      }

      const data = await response.json();
      
      setUploadResult(data);
      toast.success(`Upload complete! ${data.success} customers added`);
      if (data.success > 0) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploadResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-6 flex justify-between items-center rounded-t-2xl">
          <div className="flex items-center gap-3">
            <Upload className="w-6 h-6" />
            <h2 className="text-2xl font-bold">Bulk Upload Customers</h2>
          </div>
          <button onClick={handleClose} className="hover:bg-white/20 rounded-full p-2 transition">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Instructions
            </h3>
            <ul className="text-sm text-blue-800 space-y-1 ml-7">
              <li>• Download the template file below</li>
              <li>• Fill in customer details in Excel</li>
              <li>• Required fields: firstName, lastName, mobile, email, billingAddress</li>
              <li>• Optional fields: companyName, gstin, shippingAddress, outstandingAmount</li>
              <li>• Address format: "Street, City, State, Pincode"</li>
              <li>• Upload the completed Excel file</li>
            </ul>
          </div>

          {/* Download Template */}
          <div>
            <button
              onClick={downloadTemplate}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition font-semibold"
            >
              <Download className="w-5 h-5" />
              Download Template
            </button>
          </div>

          {/* File Upload */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Upload Excel File</label>
            <div className="flex items-center gap-3">
              <label className="flex-1 cursor-pointer">
                <div className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                  file ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                }`}>
                  {file ? (
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <FileSpreadsheet className="w-6 h-6" />
                      <span className="font-medium">{file.name}</span>
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      <Upload className="w-8 h-8 mx-auto mb-2" />
                      <p className="text-sm">Click to select Excel file</p>
                      <p className="text-xs text-gray-400 mt-1">(.xlsx or .xls only)</p>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Upload Result */}
          {uploadResult && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-gray-900">Upload Results</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">{uploadResult.success} Successful</span>
                </div>
                <div className="flex items-center gap-2 text-red-600">
                  <XCircle className="w-5 h-5" />
                  <span className="font-semibold">{uploadResult.failed} Failed</span>
                </div>
              </div>
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Errors (showing first 10):</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {uploadResult.errors.map((error, idx) => (
                      <div key={idx} className="text-xs text-red-600 bg-red-50 p-2 rounded">
                        Row {error.row}: {error.email} - {error.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
            >
              {uploadResult ? 'Close' : 'Cancel'}
            </button>
            <button
              onClick={handleUpload}
              disabled={!file || loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg hover:from-green-700 hover:to-teal-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Uploading...' : 'Upload & Process'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
